"use client";

import Link from "next/link";
import { useActionState } from "react";
import { CheckCircle2 } from "lucide-react";
import { HEARD_FROM, INDIAN_STATES } from "@/lib/login-request";
import { submitLoginRequest, type RequestState } from "./actions";

const field =
  "w-full rounded-lg border border-line-strong bg-surface px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

export default function RequestForm() {
  const [state, submit, pending] = useActionState<RequestState, FormData>(submitLoginRequest, {});

  if (state.ok) {
    return (
      <div className="rounded-lg border border-line bg-ok-tint p-5">
        <p className="flex items-center gap-2 font-semibold text-ok">
          <CheckCircle2 size={20} /> Request received
        </p>
        <p className="mt-2 text-sm text-text">
          Thank you. Sportsun will call you on <span className="nums font-semibold">+91 {state.phone}</span> within
          a working day. Once your account is set up, you will log in with this number.
        </p>
        <Link href="/login" className="mt-4 inline-block text-sm font-semibold text-brand hover:underline">
          Back to login
        </Link>
      </div>
    );
  }

  return (
    <form action={submit} className="space-y-4">
      {/* Honeypot: hidden from people, filled by bots. */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />

      <Field label="Shop / business name" name="businessName" required autoComplete="organization" />
      <Field label="Owner's name" name="ownerName" required autoComplete="name" />
      <div>
        <label htmlFor="phone" className="mb-1 block text-sm font-medium text-text">
          Mobile number <span className="text-danger">*</span>
        </label>
        <div className="flex items-center rounded-lg border border-line-strong bg-surface focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20">
          <span className="nums pl-3 text-muted">+91</span>
          <input id="phone" name="phone" inputMode="numeric" autoComplete="tel" maxLength={13} required className="nums w-full bg-transparent px-3 py-2.5 text-sm outline-none" />
        </div>
        <p className="mt-1 text-xs text-muted">Your login will be sent to this number.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="City" name="city" required autoComplete="address-level2" />
        <div>
          <label htmlFor="state" className="mb-1 block text-sm font-medium text-text">
            State <span className="text-danger">*</span>
          </label>
          <select id="state" name="state" required defaultValue="" className={field}>
            <option value="" disabled>
              Pick a state
            </option>
            {INDIAN_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>
      <Field label="GSTIN (optional)" name="gstin" autoComplete="off" placeholder="07ABCDE1234F1Z5" />
      <div>
        <label htmlFor="heardFrom" className="mb-1 block text-sm font-medium text-text">
          How did you hear about Sportsun?
        </label>
        <select id="heardFrom" name="heardFrom" defaultValue="" className={field}>
          <option value="">—</option>
          {HEARD_FROM.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="note" className="mb-1 block text-sm font-medium text-text">
          Anything else
        </label>
        <textarea id="note" name="note" rows={3} maxLength={1000} placeholder="What you sell, which brands, how long you have been trading…" className={field} />
      </div>

      {state.error && <p className="rounded-md bg-danger-tint px-3 py-2 text-sm text-danger">{state.error}</p>}

      <button
        type="submit"
        disabled={pending}
        className="w-full rounded-lg bg-brand px-4 py-3 font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-60"
      >
        {pending ? "Sending…" : "Request a login"}
      </button>
      <p className="text-center text-xs text-muted">
        Already a dealer?{" "}
        <Link href="/login" className="font-semibold text-brand hover:underline">
          Log in
        </Link>
      </p>
    </form>
  );
}

function Field({ label, name, required, autoComplete, placeholder }: { label: string; name: string; required?: boolean; autoComplete?: string; placeholder?: string }) {
  return (
    <div>
      <label htmlFor={name} className="mb-1 block text-sm font-medium text-text">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      <input id={name} name={name} required={required} autoComplete={autoComplete} placeholder={placeholder} maxLength={120} className={field} />
    </div>
  );
}
